import { localize } from '../i18n';
import SingleLineText from './ui/SingleLineText';
import Button from './ui/FitButton';
import React, { useState, useLayoutEffect } from 'react';
import { Box, Typography, Switch } from '@mui/material';
import BottomSheet from './BottomSheet';
import SnoozeSection from '../pages/medication-form/reminder/SnoozeSection';

/**
 * 집요한 알림 편집 시트 — 확인 페이지의 알림 카드에서 "집요한 알림" 수정 시 노출.
 */
export default function EditSnoozeSheet({ open, form, onClose, onSave }) {
    const [staging, setStaging] = useState(null);

    useLayoutEffect(() => {
        if (open) {
            setStaging({
                reminderEnabled: form.reminderEnabled,
                reminderIntervalMinutes: form.reminderIntervalMinutes,
                maxReminderMinutes: form.maxReminderMinutes,
            });
        }
    }, [open, form]);

    if (!staging) return <BottomSheet open={open} onClose={onClose}>{null}</BottomSheet>;

    const updateStaging = (updater) => {
        setStaging(prev => typeof updater === 'function' ? updater(prev) : updater);
    };

    const interval = staging.reminderIntervalMinutes;
    const max = staging.maxReminderMinutes;
    const isInvalid = staging.reminderEnabled
        && (!interval || interval <= 0 || !max || max < interval);

    const handleSave = () => {
        onSave({
            reminderEnabled: staging.reminderEnabled,
            reminderIntervalMinutes: staging.reminderEnabled ? interval : 0,
            maxReminderMinutes: staging.reminderEnabled ? max : 0,
        });
        onClose();
    };

    return (
        <BottomSheet open={open} onClose={onClose} footer={
            <>
                <Button
                    fullWidth
                    disabled={isInvalid}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleSave}
                    sx={{
                        bgcolor: '#ffe566', color: '#5a4a00',
                        borderRadius: 3, py: 1.6, fontSize: '1.1rem', fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(230,168,0,0.3)',
                        '&:hover': { bgcolor: '#e8d040' },
                        '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#888', boxShadow: 'none' },
                    }}
                >{localize("저장")}</Button>
            </>
        }>
            <SingleLineText sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1a1a1a', mb: 2 }}>{localize("집요한 알림 수정")}</SingleLineText>
            <Box
                sx={{
                    p: 1.45,
                    borderRadius: 3,
                    bgcolor: '#fff',
                    border: '1px solid #e5e7eb',
                    mb: staging.reminderEnabled ? 1.4 : 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: '#1a1a1a', lineHeight: 1.35 }}>
                            {staging.reminderEnabled ? localize("켜짐") : localize("꺼짐")}
                        </Typography>
                        <Typography sx={{ mt: 0.35, fontSize: '0.88rem', color: '#777', lineHeight: 1.45, fontWeight: 750, wordBreak: 'keep-all' }}>
                            {staging.reminderEnabled ? localize("먹었다고 확인할 때까지 다시 알려드려요.") : localize("첫 알림 한 번만 보내요.")}
                        </Typography>
                    </Box>
                    <Switch
                        inputProps={{ 'aria-label': localize('집요한 알림 켜기 끄기') }}
                        checked={staging.reminderEnabled}
                        onChange={(event) => {
                            const checked = event.target.checked;
                            updateStaging(prev => ({
                                ...prev,
                                reminderEnabled: checked,
                                reminderIntervalMinutes: checked && Number(prev.reminderIntervalMinutes) <= 0 ? 30 : prev.reminderIntervalMinutes,
                                maxReminderMinutes: checked && Number(prev.maxReminderMinutes) <= 0 ? 120 : prev.maxReminderMinutes,
                            }));
                        }}
                        sx={{
                            width: 52,
                            height: 34,
                            p: 0,
                            flexShrink: 0,
                            '& .MuiSwitch-switchBase': {
                                p: '4px',
                                '&.Mui-checked': {
                                    transform: 'translateX(18px)',
                                    color: '#fff',
                                    '& + .MuiSwitch-track': {
                                        bgcolor: '#ffe566',
                                        borderColor: '#e6c800',
                                        opacity: 1,
                                    },
                                },
                            },
                            '& .MuiSwitch-thumb': {
                                width: 26,
                                height: 26,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                            },
                            '& .MuiSwitch-track': {
                                borderRadius: 999,
                                bgcolor: '#e5e7eb',
                                border: '1px solid #d1d5db',
                                opacity: 1,
                            },
                        }}
                    />
                </Box>
            </Box>
            {staging.reminderEnabled && (
                <SnoozeSection form={staging} setForm={updateStaging} hideToggle />
            )}
        </BottomSheet>
    );
}
